import "./styles/main.css";

import { onAuthStateChanged, getRedirectResult } from "firebase/auth";

import { auth } from "./lib/firebase";
import { state } from "./lib/state";
import { showToast, formatError } from "./lib/dom";
import { hydrateLibraries } from "./lib/storage";
import {
  bindNavigation,
  onScreenChange,
  parseScreenFromHash,
  setActiveScreen,
} from "./lib/router";
import {
  identifyUser,
  initializeAnalytics,
  resetAnalytics,
  trackEvent,
} from "./lib/analytics";

import * as overview from "./screens/overview";
import * as account from "./screens/account";
import * as studio from "./screens/studio";
import * as hair from "./screens/hair";
import * as closet from "./screens/closet";
import * as billing from "./screens/billing";
import * as faq from "./screens/faq";

import {
  loadProducts,
  refreshBillingState,
  reportCheckoutOutcome,
  resumePendingCheckout,
} from "./screens/billing";
import {
  renderPersonLibraries,
  renderGarmentLibrary,
  renderStyleSelection,
  syncPreviewEmptyStates,
  prefetchTrendingThumbnails,
} from "./screens/studio";
import { applyAuthStateUI } from "./screens/account";
import { clearProjectsPolling, evaluateClosetPolling } from "./screens/closet";

onAuthStateChanged(auth, async user => {
  const previousUid = state.user?.uid ?? null;
  state.user = user;

  // Get a fresh token when auth state changes
  if (user) {
    try {
      state.idToken = await user.getIdToken();
      console.log("Auth state changed: signed in with fresh token", {
        userId: user.uid.substring(0, 8) + "...",
        tokenLength: state.idToken?.length || 0,
      });
    } catch (error) {
      console.error("Failed to get fresh token on auth state change:", error);
      state.idToken = null;
    }

    identifyUser({
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      signupDateIso: user.metadata?.creationTime
        ? new Date(user.metadata.creationTime).toISOString()
        : null,
    });
  } else {
    state.idToken = null;
    console.log("Auth state changed: signed out");
    if (previousUid) {
      resetAnalytics();
    }
  }

  applyAuthStateUI();
  if (user) {
    await loadProducts();
  }
  await refreshBillingState();
  evaluateClosetPolling();
  if (user) {
    void resumePendingCheckout();
  }
});

async function init(): Promise<void> {
  initializeAnalytics();
  trackEvent("app_launched");

  hydrateLibraries();

  onScreenChange(screen => {
    trackEvent("screen_viewed", { screen_name: screen });
  });

  bindNavigation();
  overview.bind();
  account.bind();
  studio.bind();
  hair.bind();
  closet.bind();
  billing.bind();
  faq.bind();

  renderPersonLibraries();
  renderGarmentLibrary();
  renderStyleSelection();
  syncPreviewEmptyStates();
  applyAuthStateUI();
  setActiveScreen(parseScreenFromHash(), { updateHash: false });

  try {
    await getRedirectResult(auth);
  } catch (error: unknown) {
    console.warn("Redirect sign-in failed:", error);
    showToast(formatError(error), true);
  }

  const params = new URLSearchParams(window.location.search);
  let shouldCleanQuery = false;
  if (params.get("checkout") === "success") {
    reportCheckoutOutcome("success");
    showToast("Payment complete. Updating your account...");
    setActiveScreen("pricing");
    shouldCleanQuery = true;
  }
  if (params.get("checkout") === "cancel") {
    reportCheckoutOutcome("cancel");
    showToast("Checkout was canceled.", true);
    setActiveScreen("pricing");
    shouldCleanQuery = true;
  }
  if (shouldCleanQuery) {
    history.replaceState(
      null,
      "",
      `${window.location.pathname}${window.location.hash}`,
    );
  }

  await loadProducts();
  await refreshBillingState();

  void prefetchTrendingThumbnails();
}

window.addEventListener("beforeunload", clearProjectsPolling);

init().catch((error: unknown) => {
  console.error(error);
  showToast("Something went wrong loading the page. Please refresh.", true);
});
